import React, { useState } from 'react';
import {
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Box,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import type { BusinessLocation } from 'src/api/types/business-types';
import { useTranslation } from 'src/text';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  upgradeLocation,
  useLocationSubscriptionDoc,
} from 'src/api/services/billing';
import { locationPlans, LocationPlan, LocationPlanId } from '../plans';
import { CONTACT_US_URL } from 'src/app/constants';
import {
  featureList,
  planCard,
  planGrid,
  popularPlanCard,
  locationCard,
  headerActions,
  expandButton,
  planBody,
  priceFrequency,
  buttonLoader,
} from '../styles';

interface LocationBillingRowProps {
  location: BusinessLocation;
  billingCycle: 'monthly' | 'yearly';
}

export const LocationBillingRow: React.FC<LocationBillingRowProps> = ({
  location,
  billingCycle,
}) => {
  const { billingLabels } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const { info, loading } = useLocationSubscriptionDoc(location.key);

  const status = info?.status ?? 'none';
  const isActive =
    status === 'active' || status === 'trialing' || status === 'past_due';
  const currentPlanId =
    (info?.planId as LocationPlanId | undefined) || undefined;
  const planRank: Record<LocationPlanId, number> = {
    essential: 1,
    growth: 2,
    elite: 3,
  };

  const chipColor =
    status === 'active'
      ? 'success'
      : status === 'trialing'
        ? 'info'
        : status === 'trial_expired'
          ? 'warning'
          : 'default';

  const locality = location.storefrontAddress?.locality?.trim();
  const title = locality ? `${location.title} (${locality})` : location.title;

  const statusLabel =
    status === 'none'
      ? billingLabels.statusNoSubscription
      : status === 'trialing'
        ? billingLabels.statusTrial
        : status === 'trial_expired'
          ? billingLabels.statusTrialExpired
          : status === 'active'
            ? billingLabels.statusActive
            : status;

  const handlePlanClick = async (plan: LocationPlan) => {
    if (loading) return;

    setLoadingPlan(plan.id);
    try {
      await upgradeLocation(location.key, billingCycle, plan.id);
    } finally {
      setLoadingPlan(null);
    }
  };

  const handleUnsubscribe = () => {
    window.open(CONTACT_US_URL, '_blank');
  };
  // Bilal
  return (
    <Paper key={location.key} elevation={0} sx={locationCard}>
      <Stack spacing={expanded ? 2 : 1}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          spacing={2}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton
              size="small"
              onClick={() => setExpanded(prev => !prev)}
              sx={{
                ...expandButton,
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              }}>
              <ExpandMoreIcon fontSize="small" />
            </IconButton>
            <Stack spacing={0.5}>
              <Typography variant="subtitle1">{title}</Typography>
              <Typography variant="caption" color="text.secondary">
                {location.storeCode || location.locationId}
              </Typography>
            </Stack>
          </Stack>
          <Stack
            direction={{ xs: 'row', sm: 'row' }}
            spacing={1}
            alignItems="center"
            sx={headerActions}>
            {loading ? (
              <CircularProgress size={18} />
            ) : (
              <Chip label={statusLabel} color={chipColor} size="small" />
            )}
          </Stack>
        </Stack>

        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box sx={planGrid}>
            {locationPlans.map(plan => {
              const planCopy = billingLabels.plans?.[plan.id];
              if (!planCopy) return null;
              const isLoadingPlan = loadingPlan === plan.id;
              const relation: 'current' | 'upgrade' | 'downgrade' | 'start' =
                !currentPlanId || !isActive
                  ? 'start'
                  : currentPlanId === plan.id
                    ? 'current'
                    : planRank[plan.id] > planRank[currentPlanId]
                      ? 'upgrade'
                      : 'downgrade';
              const buttonLabel =
                relation === 'current'
                  ? 'Unsubscribe'
                  : relation === 'upgrade'
                    ? 'Upgrade'
                    : relation === 'downgrade'
                      ? 'Downgrade'
                      : planCopy.cta;
              const isDisabled = loading || isLoadingPlan;
              const price = planCopy.prices[billingCycle];

              return (
                <Paper
                  key={plan.id}
                  elevation={plan.popular ? 2 : 0}
                  sx={plan.popular ? popularPlanCard : planCard}>
                  <Stack spacing={1.5} sx={planBody}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="subtitle1">
                        {planCopy.name}
                      </Typography>
                      {plan.popular && (
                        <Chip
                          label={billingLabels.planPopular}
                          color="primary"
                          size="small"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      )}
                    </Stack>
                    <Box>
                      <Typography variant="h5" component="div">
                        {price.display}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={priceFrequency}>
                        {price.frequency}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {planCopy.description}
                    </Typography>
                    <Box component="ul" sx={featureList}>
                      {planCopy.features.map(feature => (
                        <Typography
                          key={feature}
                          component="li"
                          variant="body2"
                          color="text.secondary">
                          {feature}
                        </Typography>
                      ))}
                    </Box>
                    <Button
                      fullWidth
                      variant={
                        relation === 'current' ? 'outlined' : 'contained'
                      }
                      color={relation === 'current' ? 'warning' : 'primary'}
                      onClick={
                        relation === 'current'
                          ? handleUnsubscribe
                          : () => handlePlanClick(plan)
                      }
                      disabled={isDisabled}
                      sx={{ mt: 'auto' }}>
                      {isLoadingPlan ? (
                        <>
                          <CircularProgress
                            size={18}
                            color="inherit"
                            sx={buttonLoader}
                          />
                          {buttonLabel}
                        </>
                      ) : (
                        buttonLabel
                      )}
                    </Button>
                  </Stack>
                </Paper>
              );
            })}
          </Box>
        </Collapse>
      </Stack>
    </Paper>
  );
};
